import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * Auth hook wrapping Supabase Auth state.
 *
 * Uses email OTP (6-digit code) instead of magic links to avoid redirect-URL
 * issues during development. The flow:
 *   1. User enters email → sendOtp(email) sends a 6-digit code
 *   2. User enters the code → verifyOtp(email, code) completes sign-in
 *
 * Passkeys are beta/opt-in (spec 05 §1) and available via signInWithPasskey.
 */
export interface UseAuth {
  session: Session | null;
  user: User | null;
  loading: boolean;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  signInWithOAuth: (provider: "github" | "google" | "apple") => Promise<void>;
  signInWithPasskey: () => Promise<void>;
  signOut: () => Promise<void>;
}

export function useAuth(): UseAuth {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  /** Send a 6-digit OTP code to the given email (passwordless). */
  const sendOtp = useCallback(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        // The email template on free tier sends a magic link.
        // This redirect URL must match the Site URL in Supabase Dashboard.
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;
  }, []);

  /** Verify the 6-digit OTP code sent to the email. */
  const verifyOtp = useCallback(async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: "email",
    });
    if (error) throw error;
    // Session is set automatically by verifyOtp, but update state immediately.
    if (data.session) setSession(data.session);
  }, []);

  /** OAuth redirect (spec 06 §2 screen 4). */
  const signInWithOAuth = useCallback(
    async (provider: "github" | "google" | "apple") => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    },
    [],
  );

  /**
   * Passkey sign-in (beta, opt-in). Uses WebAuthn via Supabase Auth.
   */
  const signInWithPasskey = useCallback(async () => {
    const authClient = supabase.auth as unknown as {
      signInWithWebAuthn?: () => Promise<{ error: unknown }>;
    };
    if (typeof authClient.signInWithWebAuthn !== "function") {
      throw new Error("passkey-unsupported");
    }
    const { error } = await authClient.signInWithWebAuthn();
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  return {
    session,
    user: session?.user ?? null,
    loading,
    sendOtp,
    verifyOtp,
    signInWithOAuth,
    signInWithPasskey,
    signOut,
  };
}
